/**
 * Game-type budget module — computes per-rotation targets and deficits from
 * the live roster and session match history, with no database access required.
 *
 * Capacity formula (from current waitlist pool):
 *   maxCourts.womens = floor(femaleCount / 4)
 *   maxCourts.mens   = floor(maleCount / 4)
 *   maxCourts.mixed  = floor(min(femaleCount, maleCount) / 2)
 *
 * Target shares are capacity-normalized — a type with more capacity gets a
 * proportionally larger target share, reflecting what the roster can support:
 *   weight[type]      = maxCourts[type]  (0 if below min-player threshold)
 *   targetShare[type] = weight[type] / sum(weights)
 *
 * deficit[type] = targetShare[type] * totalDoublesGames - gameCounts[type]
 *
 * The type with the largest deficit that is feasible in the current pool and
 * exceeds deficitThreshold becomes priorityGameType.
 */

import { MATCHMAKING_CONFIG } from './matchmakingConfig.js';

/**
 * Total finished doubles games in session history (mixed + mens + womens).
 * @param {Object} sessionState
 * @param {Object} playersById
 * @returns {number}
 */
export function getSessionDoublesGameCount(sessionState, playersById) {
  const counts = countGamesByType(sessionState, playersById);
  return counts.mixed + counts.mens + counts.womens;
}

/**
 * Count session games by type from matchHistory.
 */
function countGamesByType(sessionState, playersById) {
  const counts = { mixed: 0, mens: 0, womens: 0 };
  if (!sessionState?.matchHistory?.length || !playersById) return counts;

  for (const game of sessionState.matchHistory) {
    let team1 = game.team1;
    let team2 = game.team2;
    if ((!Array.isArray(team1) || !Array.isArray(team2)) && Array.isArray(game.teams) && game.teams.length >= 2) {
      [team1, team2] = game.teams;
    }
    if (!Array.isArray(team1) || !Array.isArray(team2)) continue;
    if (team1.length !== 2 || team2.length !== 2) continue;

    const allIds = [...team1, ...team2];
    const genders = allIds.map(id => (playersById[id]?.gender || '').toLowerCase());
    if (genders.length !== 4) continue;

    const allFemale = genders.every(g => g === 'female');
    const allMale = genders.every(g => g === 'male');

    if (allFemale) counts.womens += 1;
    else if (allMale) counts.mens += 1;
    else counts.mixed += 1;
  }
  return counts;
}

/**
 * Compute the game-type budget for a single matchmaking fill.
 *
 * @param {Object} params
 * @param {Object} params.playersById         - Full player map (id → player object with .gender)
 * @param {string[]} params.playerIds         - Players currently in the pool for this fill
 * @param {number} params.numberOfCourts      - Courts available (often 1 for per-court fills)
 * @param {Object} params.sessionState        - Session state (needs .matchHistory)
 * @param {Object} [params.config]            - Optional override for MATCHMAKING_CONFIG.gameTypeBalance
 * @returns {GameTypeBudget}
 */
export function computeGameTypeBudget({ playersById, playerIds, numberOfCourts, sessionState, config = null }) {
  const balanceConfig = config ?? MATCHMAKING_CONFIG.gameTypeBalance ?? {};

  const femaleCount = playerIds.filter(id => (playersById[id]?.gender || '').toLowerCase() === 'female').length;
  const maleCount   = playerIds.length - femaleCount;

  const minForLevels = balanceConfig.minPlayersForLevelsCourt ?? balanceConfig.womensMinFemaleCount ?? 4;
  const minPerGenderMixed = balanceConfig.minPlayersPerGenderForMixed ?? 2;

  // Max courts each type could fill this rotation given the current pool.
  const maxCourtsThisRotation = {
    womens: femaleCount >= minForLevels ? Math.floor(femaleCount / 4) : 0,
    mens:   maleCount   >= minForLevels ? Math.floor(maleCount   / 4) : 0,
    mixed:  (femaleCount >= minPerGenderMixed && maleCount >= minPerGenderMixed)
              ? Math.floor(Math.min(femaleCount, maleCount) / 2)
              : 0,
  };

  // Capacity-normalized target shares — types with zero capacity get 0.
  const totalWeight = maxCourtsThisRotation.womens + maxCourtsThisRotation.mens + maxCourtsThisRotation.mixed;
  const targetShare = {
    womens: totalWeight > 0 ? maxCourtsThisRotation.womens / totalWeight : 0,
    mens:   totalWeight > 0 ? maxCourtsThisRotation.mens   / totalWeight : 0,
    mixed:  totalWeight > 0 ? maxCourtsThisRotation.mixed  / totalWeight : 0,
  };

  const gameCounts = countGamesByType(sessionState, playersById);
  const totalDoublesGames = gameCounts.mixed + gameCounts.mens + gameCounts.womens;

  const deficit = {
    womens: targetShare.womens * totalDoublesGames - gameCounts.womens,
    mens:   targetShare.mens   * totalDoublesGames - gameCounts.mens,
    mixed:  targetShare.mixed  * totalDoublesGames - gameCounts.mixed,
  };

  // Pick the type with the largest qualifying deficit.
  const deficitThreshold = balanceConfig.deficitThreshold ?? 0.75;
  const enabled = balanceConfig.enabled !== false;
  const minGamesForCourtHint = balanceConfig.minSessionDoublesGamesForCourtHint ?? 10;
  const courtHintEligible = totalDoublesGames >= minGamesForCourtHint;

  let priorityGameType = null;
  if (enabled && courtHintEligible) {
    let bestDeficit = deficitThreshold - 0.001; // must strictly exceed threshold
    for (const type of ['womens', 'mens', 'mixed']) {
      if (maxCourtsThisRotation[type] >= 1 && deficit[type] > bestDeficit) {
        bestDeficit = deficit[type];
        priorityGameType = type;
      }
    }
  }

  const shouldPrioritizeCourt = priorityGameType !== null;

  return {
    femaleCount,
    maleCount,
    gameCounts,
    totalDoublesGames,
    minSessionDoublesGamesForCourtHint: minGamesForCourtHint,
    courtHintEligible,
    maxCourtsThisRotation,
    targetShare,
    deficit,
    priorityGameType,
    shouldPrioritizeCourt,
    // Deprecated aliases — kept for backward-compat with existing debug UI.
    womensDeficit: deficit.womens,
    shouldPrioritizeWomensCourt: priorityGameType === 'womens',
  };
}
